import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useLines } from '../../hooks/useLines';
import { usePermission } from '../../hooks/usePermission';
import { date, money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { PeriodBar, financialYear } from '../../components/accounts/PeriodBar';
import { VoidDialog } from '../../components/ui/VoidDialog';
import { useToast } from '../../components/ui/Toast';
import { DateInput, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';
import { CellInput, CellSelect, LineRow, LineTable } from '../../components/ui/LineTable';

interface ExpenseRow {
  id: string;
  doc_no: string;
  doc_date: string;
  payee: string;
  narration: string;
  categories: string;
  payment_mode: string;
  total_amount: string;
  status: string;
}

interface Category {
  id: string;
  code: string;
  name: string;
}

interface Line {
  accountId: string;
  description: string;
  amount: string;
}

const blankLine = (): Line => ({ accountId: '', description: '', amount: '' });

const STATUS_TONE: Record<string, string> = {
  PAID: 'text-success',
  POSTED: 'text-gold-700',
  CANCELLED: 'text-danger',
};

/**
 * Rent, salary, electricity — money out that does not buy stock.
 *
 * Until this screen existed the P&L showed gross profit and called it net
 * profit, because these had nowhere to go.
 */
export function ExpensesPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const toast = useToast();
  const { branches, defaultBranchId } = useBranches();
  const lines = useLines<Line>(blankLine);

  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(financialYear());
  const [error, setError] = useState<unknown>(null);

  const [form, setForm] = useState({
    docDate: today(),
    supplierId: '',
    payeeName: '',
    narration: '',
    referenceNo: '',
    paymentMode: 'CASH',
    taxAmount: '',
    branchId: '',
  });

  const list = useQuery({
    queryKey: ['expenses', range.from, range.to],
    queryFn: () => api.get<{ data: ExpenseRow[] }>(`/expenses?from=${range.from}&to=${range.to}`),
  });
  const byCategory = useQuery({
    queryKey: ['expense-summary', range.from, range.to],
    queryFn: () =>
      api.get<{
        rows?: { category: string; amount: string; bills: string }[];
        data?: { category: string; amount: string; bills: string }[];
      }>(`/reports/expense-summary/run?from=${range.from}&to=${range.to}`),
    enabled: can('report.view_financial'),
  });
  const categories = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: () => api.get<{ data: Category[] }>('/expense-categories'),
    staleTime: 600_000,
  });
  const suppliers = useQuery({ queryKey: ['suppliers', ''], queryFn: () => endpoints.parties.suppliers() });

  const supplierList = (suppliers.data?.data ?? []) as unknown as { id: string; name: string }[];
  const categoryOptions = (categories.data?.data ?? []).map((c) => ({ value: c.id, label: `${c.code} ${c.name}` }));

  const subtotal = lines.values.reduce((a, l) => a + Number(l.amount || 0), 0);
  const total = subtotal + Number(form.taxAmount || 0);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ docNo: string; totalAmount: string }>(
        '/expenses',
        {
          branchId: form.branchId || defaultBranchId,
          docDate: form.docDate,
          ...(form.supplierId ? { supplierId: form.supplierId } : {}),
          ...(form.payeeName ? { payeeName: form.payeeName } : {}),
          narration: form.narration,
          ...(form.referenceNo ? { referenceNo: form.referenceNo } : {}),
          paymentMode: form.paymentMode,
          ...(form.taxAmount ? { taxAmount: form.taxAmount } : {}),
          lines: lines.values.map((l) => ({
            accountId: l.accountId,
            ...(l.description ? { description: l.description } : {}),
            amount: l.amount,
          })),
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: (result) => {
      toast(`${result.docNo} recorded`, `${money(result.totalAmount)} — already in the profit and loss`);
      setOpen(false);
      lines.reset();
      setForm({ ...form, narration: '', payeeName: '', referenceNo: '', taxAmount: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['expenses'] });
    },
    onError: setError,
  });

  const [voiding, setVoiding] = useState<{ id: string; doc_no: string } | null>(null);
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/expenses/${id}/cancel`, { reason }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['expenses'] });
      setVoiding(null);
    },
    onError: (e) => {
      setError(e);
      setVoiding(null);
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const rows = list.data?.data ?? [];
  const live = rows.filter((e) => e.status !== 'CANCELLED');
  const todayIso = today();
  const todayTotal = live
    .filter((e) => e.doc_date.slice(0, 10) === todayIso)
    .reduce((a, e) => a + Number(e.total_amount), 0);
  const periodTotal = live.reduce((a, e) => a + Number(e.total_amount), 0);
  const summary = (byCategory.data?.rows ?? byCategory.data?.data ?? []).filter((c) => Number(c.amount) > 0);
  const biggest = summary.reduce((a, c) => Math.max(a, Number(c.amount)), 0);
  const ready =
    form.narration.trim().length >= 3 &&
    (form.supplierId !== '' || form.payeeName.trim() !== '') &&
    lines.values.every((l) => l.accountId && l.amount);

  return (
    <>
      <PageHeader
        title="Expenses"
        subtitle="Rent, salary, electricity — everything that leaves the business without buying stock."
        actions={
          can('expense.create') ? (
            <button className="btn-primary" onClick={() => setOpen(true)}>
              Record expense
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      <PeriodBar from={range.from} to={range.to} onChange={setRange} />

      {/* What has gone out, and on what. An owner asks both at once. */}
      {rows.length > 0 ? (
        <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <div className="grid min-w-0 grid-cols-2 gap-px overflow-hidden rounded-lg border border-silver-100 bg-silver-100">
            <Tile label="Today" value={money(todayTotal.toFixed(2))} />
            <Tile label="This period" value={money(periodTotal.toFixed(2))} strong />
          </div>

          {summary.length > 0 ? (
            <div className="min-w-0 rounded-lg border border-silver-100 bg-surface p-3 shadow-card">
              <p className="mb-2 text-2xs uppercase tracking-wide text-ink-soft">Where it went</p>
              <div className="space-y-1.5">
                {summary.slice(0, 6).map((c) => (
                  <div key={c.category} className="flex items-center gap-3 text-sm">
                    <span className="w-28 truncate sm:w-56">{c.category.replace(/^\d+\s/, '')}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-silver-100">
                      <div
                        className="h-full rounded-full bg-gold-500"
                        style={{ width: `${biggest > 0 ? (Number(c.amount) / biggest) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="num w-24 shrink-0 text-right font-medium sm:w-28">{money(c.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {list.isLoading ? <Loading /> : null}
      {!list.isLoading && rows.length === 0 ? (
        <Empty
          message="No expenses recorded yet."
          hint="Rent, salaries, electricity — until these are here, the profit figure is gross profit wearing a different name."
          action={can('expense.create') ? { label: 'Record expense', onClick: () => setOpen(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Date', 'Document', 'Paid to', 'For', 'Category', 'Mode', 'Amount', 'Status', '']}>
          {rows.map((e) => (
            <tr key={e.id}>
              <td className="td">{date(e.doc_date)}</td>
              <td className="td num">{e.doc_no}</td>
              <td className="td">{e.payee}</td>
              <td className="td text-xs">{e.narration}</td>
              <td className="td text-xs text-ink-soft">{e.categories}</td>
              <td className="td text-xs">{e.payment_mode.toLowerCase()}</td>
              <td className="td num text-right font-medium">{money(e.total_amount)}</td>
              <td className={`td text-xs ${STATUS_TONE[e.status] ?? ''}`}>{e.status.toLowerCase()}</td>
              <td className="td">
                {e.status !== 'CANCELLED' && can('document.void') ? (
                  <button className="text-xs text-danger underline" onClick={() => setVoiding(e)}>
                    Void
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <div className="rounded-xl border border-silver-100 bg-surface p-5 shadow-card">
          <div>
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold">Record an expense</h2>
                <p className="mt-1 text-sm text-ink-soft">
                  One payment can be split across categories, so the P&amp;L attributes it properly instead of lumping
                  it under miscellaneous.
                </p>
              </div>
              <button className="text-sm text-ink-soft underline" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>

            <ErrorNotice error={error} />

            <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-5">
              <div className="card grid gap-3 md:grid-cols-3">
                <Field label="Date" required>
                  <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} />
                </Field>

                <Field label="Supplier" hint="Leave blank and type a name below if they are not on the books.">
                  <Select
                    value={form.supplierId}
                    onChange={(v) => setForm({ ...form, supplierId: v })}
                    options={supplierList.map((s) => ({ value: s.id, label: s.name }))}
                  />
                </Field>

                <Field label="Or paid to" hint="The electricity board is not a supplier anybody wants a ledger for.">
                  <TextInput
                    value={form.payeeName}
                    onChange={(v) => setForm({ ...form, payeeName: v })}
                    placeholder="Chennai Electricity Board"
                  />
                </Field>

                <div className="md:col-span-2">
                  <Field
                    label="What it was for"
                    required
                    hint="Kept permanently. An unexplained expense is how money leaves quietly."
                  >
                    <TextInput
                      value={form.narration}
                      onChange={(v) => setForm({ ...form, narration: v })}
                      placeholder="Electricity bill for April"
                    />
                  </Field>
                </div>

                <Field label="Reference">
                  <TextInput value={form.referenceNo} onChange={(v) => setForm({ ...form, referenceNo: v })} />
                </Field>

                <Field label="Paid by" required>
                  <Select
                    value={form.paymentMode}
                    onChange={(v) => setForm({ ...form, paymentMode: v })}
                    options={[
                      { value: 'CASH', label: 'Cash' },
                      { value: 'BANK', label: 'Bank transfer' },
                      { value: 'UPI', label: 'UPI' },
                      { value: 'CARD', label: 'Card' },
                      { value: 'CHEQUE', label: 'Cheque' },
                      { value: 'CREDIT', label: 'Not yet paid (owed)' },
                    ]}
                  />
                </Field>

                <Field label="GST on the bill" hint="Claimable, so it goes to input tax rather than into the cost.">
                  <NumberInput
                    value={form.taxAmount}
                    onChange={(v) => setForm({ ...form, taxAmount: v })}
                    placeholder="0.00"
                    suffix="₹"
                  />
                </Field>

                {branches.length > 1 ? (
                  <Field label="Branch" required>
                    <Select
                      value={form.branchId || (defaultBranchId ?? '')}
                      onChange={(v) => setForm({ ...form, branchId: v })}
                      options={branches.map((b) => ({ value: b.id, label: b.name }))}
                    />
                  </Field>
                ) : null}
              </div>

              {form.paymentMode === 'CREDIT' && !form.supplierId ? (
                <p className="rounded border border-gold-400 bg-gold-50 p-2 text-xs">
                  An expense left unpaid has to be owed to somebody on the books. Choose a supplier, or record how it
                  was paid.
                </p>
              ) : null}

              <LineTable
                head={['Category', 'Description', 'Amount']}
                addLabel="Split across another category"
                onAdd={lines.add}
                footer={
                  <tfoot className="bg-gold-50/60">
                    <tr>
                      <td className="td" colSpan={3} />
                      <td className="td num text-right font-semibold">{money(subtotal.toFixed(2))}</td>
                      <td className="td" />
                    </tr>
                  </tfoot>
                }
              >
                {lines.lines.map((line, index) => (
                  <LineRow
                    key={line.key}
                    index={index}
                    removable={lines.lines.length > 1}
                    onRemove={() => lines.remove(line.key)}
                  >
                    <td className="td">
                      <CellSelect
                        width="w-56"
                        value={line.accountId}
                        onChange={(v) => lines.update(line.key, { accountId: v })}
                        options={categoryOptions}
                        placeholder="Category"
                      />
                    </td>
                    <td className="td">
                      <CellInput
                        width="w-56"
                        value={line.description}
                        onChange={(v) => lines.update(line.key, { description: v })}
                      />
                    </td>
                    <td className="td">
                      <CellInput
                        numeric
                        decimals={2}
                        value={line.amount}
                        onChange={(v) => lines.update(line.key, { amount: v })}
                        placeholder="0.00"
                      />
                    </td>
                  </LineRow>
                ))}
              </LineTable>

              <div className="flex items-end justify-between">
                <button className="btn-primary" disabled={!ready || create.isPending}>
                  {create.isPending ? 'Recording…' : 'Record expense'}
                </button>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-ink-soft">Total</p>
                  <p className="num text-2xl font-semibold">{money(total.toFixed(2))}</p>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
      <VoidDialog
        docNo={voiding?.doc_no ?? null}
        what="expense"
        effect="The money goes back to the cash or bank it was paid from, and the expense comes off the profit report, in a reversing entry dated today."
        busy={cancel.isPending}
        onCancel={() => setVoiding(null)}
        onConfirm={(reason) => voiding && cancel.mutate({ id: voiding.id, reason })}
      />
    </>
  );
}

function Tile({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="bg-surface px-3 py-2">
      <p className="text-2xs text-ink-soft">{label}</p>
      <p className={`num ${strong ? 'text-lg font-bold' : 'font-medium'}`}>{value}</p>
    </div>
  );
}
